import { useState, useEffect, useRef, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { apiGet, apiPost, apiDelete } from '../lib/api';
import { EntityChip, getEntityIcon, getEntityColor, type EntityType } from './EntityChip';
import { resolveEntities, type ResolvedEntity } from '../lib/entityResolve';
import { useEntityPreview } from '../contexts/EntityPreviewContext';

interface EntitySearchResult {
  entity_type: EntityType;
  entity_id: string;
  name: string;
}

interface EntityLink {
  id: string;
  source_type: EntityType;
  source_id: string;
  target_type: EntityType;
  target_id: string;
  relationship: string;
  metadata: string | null;
  sort_order: number;
  user_id: string | null;
  created_at: string;
}

interface LinkPickerProps {
  sourceType: EntityType;
  sourceId: string;
  relationship: string;
  allowedTargetTypes?: EntityType[];
  label?: string;
  placeholder?: string;
  onLinkCreated?: (link: EntityLink) => void;
  onLinkRemoved?: (linkId: string) => void;
}

export function LinkPicker({
  sourceType,
  sourceId,
  relationship,
  allowedTargetTypes,
  label,
  placeholder = 'Search to link...',
  onLinkCreated,
  onLinkRemoved,
}: LinkPickerProps) {
  const [links, setLinks] = useState<EntityLink[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<EntitySearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load existing links
  const loadLinks = useCallback(async () => {
    try {
      const data = await apiGet<EntityLink[]>(
        `/entity-links?source_type=${sourceType}&source_id=${sourceId}&relationship=${relationship}`
      );
      setLinks(data);
    } catch (err) {
      console.error('Failed to load links:', err);
    }
  }, [sourceType, sourceId, relationship]);

  useEffect(() => { loadLinks(); }, [loadLinks]);

  // Search entities
  useEffect(() => {
    if (!showSearch || query.trim().length === 0) {
      setResults([]);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        let data = await apiGet<EntitySearchResult[]>(`/entity-search?q=${encodeURIComponent(query)}&limit=10`);
        // Filter by allowed target types
        if (allowedTargetTypes) {
          data = data.filter(r => allowedTargetTypes.includes(r.entity_type));
        }
        // Filter out already-linked entities
        const linkedIds = new Set(links.map(l => l.target_id));
        data = data.filter(r => !linkedIds.has(r.entity_id));
        setResults(data);
        setSelectedIndex(0);
      } catch (err) {
        console.error('Search failed:', err);
      } finally {
        setSearching(false);
      }
    }, 200);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, showSearch, allowedTargetTypes, links]);

  // Outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSearch(false);
        setQuery('');
        setResults([]);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Focus input when search opens
  useEffect(() => {
    if (showSearch) {
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [showSearch]);

  async function addLink(result: EntitySearchResult) {
    try {
      const link = await apiPost<EntityLink>('/entity-links', {
        source_type: sourceType,
        source_id: sourceId,
        target_type: result.entity_type,
        target_id: result.entity_id,
        relationship,
      });
      setLinks(prev => [...prev, link]);
      setQuery('');
      setResults([]);
      onLinkCreated?.(link);
    } catch (err) {
      console.error('Failed to create link:', err);
    }
  }

  async function removeLink(linkId: string) {
    try {
      await apiDelete(`/entity-links/${linkId}`);
      setLinks(prev => prev.filter(l => l.id !== linkId));
      onLinkRemoved?.(linkId);
    } catch (err) {
      console.error('Failed to remove link:', err);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) addLink(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowSearch(false);
      setQuery('');
      setResults([]);
    }
  }

  // Batch-resolve linked entity titles/snippets/existence via the entity-links resolve endpoint
  const { openPreview } = useEntityPreview();
  const [resolved, setResolved] = useState<Map<string, ResolvedEntity>>(new Map());

  useEffect(() => {
    const unknown = links.filter(l => !resolved.has(l.target_id));
    if (unknown.length === 0) return;
    let cancelled = false;
    resolveEntities(unknown.map(l => ({ type: l.target_type, id: l.target_id })))
      .then(rs => {
        if (cancelled) return;
        setResolved(prev => {
          const next = new Map(prev);
          rs.forEach(r => next.set(r.id, r));
          return next;
        });
      })
      .catch(err => console.error('Failed to resolve links:', err));
    return () => { cancelled = true; };
  }, [links]);

  return (
    <div ref={containerRef} className="relative">
      {label && (
        <label className="block text-xs text-text-muted mb-1.5 uppercase tracking-wide">
          {label}
        </label>
      )}

      {/* Existing linked entities */}
      <div
        className="min-h-[38px] px-3 py-1.5 bg-bg-input border border-border rounded-md flex flex-wrap gap-1.5 items-center cursor-pointer hover:border-border-hover transition-colors"
        onClick={() => setShowSearch(true)}
      >
        {links.length === 0 && !showSearch && (
          <span className="text-sm text-text-muted flex items-center gap-1">
            <Plus size={12} /> {placeholder}
          </span>
        )}
        {links.map(link => {
          const r = resolved.get(link.target_id);
          return (
            <span key={link.id} onClick={e => e.stopPropagation()}>
              <EntityChip
                entityType={link.target_type}
                name={r?.title ?? '…'}
                snippet={r?.snippet}
                exists={r?.exists ?? true}
                onClick={() => openPreview(link.target_type, link.target_id)}
                onRemove={() => removeLink(link.id)}
              />
            </span>
          );
        })}
        {showSearch && (
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 min-w-[100px] bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
            placeholder={placeholder}
          />
        )}
      </div>

      {/* Search results dropdown */}
      {showSearch && results.length > 0 && (
        <div className="absolute z-30 top-full left-0 mt-1 w-full max-h-48 overflow-y-auto bg-bg-surface border border-border rounded-lg shadow-lg">
          {results.map((result, idx) => {
            const Icon = getEntityIcon(result.entity_type);
            const color = getEntityColor(result.entity_type);
            return (
              <button
                key={`${result.entity_type}-${result.entity_id}`}
                onClick={() => addLink(result)}
                className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition-colors ${
                  idx === selectedIndex ? 'bg-bg-hover' : 'hover:bg-bg-hover'
                }`}
              >
                <Icon size={14} style={{ color }} className="flex-shrink-0" />
                <span className="text-text-primary truncate">{result.name}</span>
                <span className="text-xs text-text-muted ml-auto flex-shrink-0">
                  {formatEntityType(result.entity_type)}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {showSearch && query.trim().length > 0 && results.length === 0 && !searching && (
        <div className="absolute z-30 top-full left-0 mt-1 w-full bg-bg-surface border border-border rounded-lg shadow-lg">
          <div className="px-3 py-2 text-sm text-text-muted">No results found</div>
        </div>
      )}

      {showSearch && searching && (
        <div className="absolute z-30 top-full left-0 mt-1 w-full bg-bg-surface border border-border rounded-lg shadow-lg">
          <div className="px-3 py-2 text-sm text-text-muted">Searching...</div>
        </div>
      )}
    </div>
  );
}

function formatEntityType(type: EntityType): string {
  switch (type) {
    case 'task': return 'Task';
    case 'meeting_instance': return 'Meeting';
    case 'meeting_series': return 'Series';
    case 'reference_doc': return 'View';
    case 'person': return 'Person';
    case 'tag': return 'Tag';
    case 'scope': return 'Project';
    case 'quick_capture': return 'Capture';
    case 'note': return 'Note';
    default: return type;
  }
}
